<template>
    <div class="new-automation-modal">
        <StepByStep
                ref="stepByStepRef"
                :steps="automationSteps"
                :validation-rules="validationRules"
                :completed-steps="completedSteps"
                @step-change="handleStepChange"
                @finish="handleFinish"
            >
                <!-- Step 1: Configurações Básicas -->
                <template #step-0="{ nextStep }">
                    <div class="step-form">

                        <div class="modal-section">
                            <h4>Status Inicial</h4>
                            <div class="radio-group">
                                <sw-radio
                                    v-model="formData.active"
                                    label="Ativada"
                                    :value="true"
                                    name="initialStatus"
                                />
                                <sw-radio
                                    v-model="formData.active"
                                    label="Desativada"
                                    :value="false"
                                    name="initialStatus"
                                />
                            </div>
                        </div>

                        <div class="modal-section">
                            <h4>Informações Básicas</h4>
                            <sw-input
                                v-model="formData.name"
                                label="Nome da Automação *"
                                placeholder="Ex: Envio Mensal de Contratos"
                            />
                        </div>

                        <div class="modal-section">
                            <h4>Descrição</h4>
                            <sw-textarea
                                v-model="formData.description"
                                label="Descreva o objetivo desta automação..."
                                rows="3"
                            />
                        </div>

                        <div class="step-actions">
                            <sw-button
                                name="Continuar"
                                color="blue"
                                @click="nextStep"
                                :disabled="!canProceedStep1"
                            />
                        </div>
                    </div>
                </template>

                <!-- Step 2: Seleção de Arquivos -->
                <template #step-1="{ nextStep, prevStep }">
                    <div class="step-form">

                        <div class="modal-section">
                            <h4>Documentos para Envio</h4>
                            <FileSelector
                                :selected-documents="formData.selectedDocuments"
                                @update:selectedDocuments="formData.selectedDocuments = $event"
                            />
                        </div>

                        <div class="step-actions">
                            <sw-button
                                name="Voltar"
                                color="gray"
                                @click="prevStep"
                            />
                            <sw-button
                                name="Continuar"
                                color="blue"
                                @click="nextStep"
                                :disabled="!canProceedStep2"
                            />
                        </div>
                    </div>
                </template>

                <!-- Step 3: Configurações de Email -->
                <template #step-2="{ nextStep, prevStep }">
                    <div class="step-form">

                        <div class="modal-section">
                            <h4>Assunto do Email</h4>
                            <sw-input
                                v-model="formData.emailSubject"
                                label="Assunto do Email *"
                                placeholder="Ex: Documentos - {DATA_ATUAL}"
                            />
                        </div>

                        <div class="modal-section">
                            <h4>Mensagem do Email</h4>
                            <QuillEditor
                                v-model="formData.emailMessage"
                                label="Mensagem do Email"
                                placeholder="Digite sua mensagem personalizada..."
                                height="200px"
                            />
                        </div>

                        <div class="modal-section">
                            <h4>Destinatários</h4>
                            <sw-tag
                                v-model="formData.recipients"
                                label="E-mails dos Destinatários *"
                                placeholder="Digite um e-mail e pressione Enter"
                                color="blue"
                                description="Digite os e-mails dos destinatários que receberão os documentos automaticamente"
                            />
                        </div>

                        <div class="step-actions">
                            <sw-button
                                name="Voltar"
                                color="gray"
                                @click="prevStep"
                            />
                            <sw-button
                                name="Continuar"
                                color="blue"
                                @click="nextStep"
                                :disabled="!canProceedStep3"
                            />
                        </div>
                    </div>
                </template>

                <!-- Step 4: Frequência -->
                <template #step-3="{ nextStep, prevStep }">
                    <div class="step-form">
                        <div class="form-section modal-section">
                            <h4>Configurações de Frequência</h4>
                            <div class="row mb_20">
                                <div class="grid_col_6 grid_col_s_12">
                                    <sw-select
                                        v-model="formData.frequency"
                                        label="Frequência *"
                                        :options="frequencyOptions"
                                        @change="onFrequencyChange"
                                    />
                                </div>
                                <div class="grid_col_6 grid_col_s_12" v-if="showTimeConfig">
                                    <sw-input
                                        v-model="formData.time"
                                        label="Horário *"
                                        :mask="['##:##']"
                                        placeholder="HH:MM"
                                        maxlength="5"
                                        icon="clock"
                                    />
                                </div>
                            </div>

                            <!-- Input de Data para Uma vez e Diário -->
                            <div class="row mb_20" v-if="showDateConfig">
                                <div class="grid_col_6 grid_col_s_12">
                                    <sw-input
                                        v-model="formData.date"
                                        label="Data *"
                                        date-view
                                        :min-date="new Date()"
                                        icon="calendar"
                                    />
                                </div>
                            </div>

                            <!-- Configuração específica por frequência -->
                            <div class="frequency-config" v-if="formData.frequency">
                                <div v-if="formData.frequency === 'weekly'" class="row mb_20">
                                    <div class="grid_col_12">
                                        <label class="form-label">Dias da Semana</label>
                                        <div class="weekdays-selector">
                                            <div
                                                v-for="day in weekdays"
                                                :key="day.value"
                                                class="weekday-item"
                                                :class="{ selected: formData.weekdays.includes(day.value) }"
                                                @click="toggleWeekday(day.value)"
                                            >
                                                {{ day.label }}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div v-if="formData.frequency === 'monthly'" class="row mb_20">
                                    <div class="grid_col_6 grid_col_s_12">
                                        <sw-select
                                            v-model="formData.monthlyType"
                                            label="Tipo de Repetição"
                                            :options="monthlyTypeOptions"
                                        />
                                    </div>
                                    <div class="grid_col_6 grid_col_s_12" v-if="formData.monthlyType === 'day'">
                                        <sw-input
                                            v-model="formData.monthDay"
                                            label="Dia do Mês"
                                            type="number"
                                            min="1"
                                            max="31"
                                        />
                                    </div>
                                </div>
                            </div>

                            <!-- Resumo da Frequência -->
                            <div class="frequency-summary" v-if="frequencySummary">
                                <div class="summary-card">
                                    <div class="summary-icon">
                                        <sw-icon name="info" />
                                    </div>
                                    <div class="summary-content">
                                        <span class="summary-label">Resumo da automação:</span>
                                        <span class="summary-text">{{ frequencySummary }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="step-actions">
                            <sw-button
                                name="Voltar"
                                color="gray"
                                @click="prevStep"
                            />
                            <sw-button
                                name="Continuar"
                                color="blue"
                                @click="nextStep"
                                :disabled="!canProceedStep4"
                            />
                        </div>
                    </div>
                </template>

                <!-- Step 5: Revisão e Confirmação -->
                <template #step-4="{ prevStep }">
                    <div class="step-form">

                        <div class="review-sections">
                            <div class="review-section">
                                <h4>Configurações Básicas</h4>
                                <div class="review-item">
                                    <span class="review-label">Nome:</span>
                                    <span class="review-value">{{ formData.name }}</span>
                                </div>
                                <div class="review-item">
                                    <span class="review-label">Status:</span>
                                    <span class="review-value status-badge" :class="{ active: formData.active }">
                                        {{ formData.active ? 'Ativada' : 'Desativada' }}
                                    </span>
                                </div>
                                <div class="review-item" v-if="formData.description">
                                    <span class="review-label">Descrição:</span>
                                    <span class="review-value">{{ formData.description }}</span>
                                </div>
                            </div>

                            <div class="review-section">
                                <h4>Arquivos Selecionados</h4>
                                <div class="selected-files-list">
                                    <div
                                        v-for="file in selectedFilesForReview"
                                        :key="file.id"
                                        class="file-item"
                                    >
                                        <sw-icon :name="getDocumentIcon(file.type)" />
                                        <span class="file-name">{{ file.name }}</span>
                                        <span class="file-type">{{ file.type }}</span>
                                    </div>
                                </div>
                            </div>

                            <div class="review-section">
                                <h4>Configurações de Email</h4>
                                <div class="review-item">
                                    <span class="review-label">Destinatários:</span>
                                    <span class="review-value">{{ formData.recipients.join(', ') }}</span>
                                </div>
                                <div class="review-item">
                                    <span class="review-label">Assunto:</span>
                                    <span class="review-value">{{ formData.emailSubject }}</span>
                                </div>
                            </div>

                            <div class="review-section">
                                <h4>Frequência</h4>
                                <div class="review-item">
                                    <span class="review-label">Frequência:</span>
                                    <span class="review-value">{{ getFrequencyLabel() }}</span>
                                </div>
                                <div class="review-item" v-if="formData.frequency === 'once'">
                                    <span class="review-label">Data/Hora:</span>
                                    <span class="review-value">{{ formatDateTime(formData.scheduleDate) }}</span>
                                </div>
                                <div v-if="['daily', 'weekly', 'monthly'].includes(formData.frequency)">
                                    <div class="review-item">
                                        <span class="review-label">Horário:</span>
                                        <span class="review-value">{{ formData.scheduleTime }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div class="step-actions">
                            <sw-button
                                name="Voltar"
                                color="gray"
                                @click="prevStep"
                            />
                            <sw-button
                                name="Criar Automação"
                                color="green"
                                @click="createAutomation"
                            />
                        </div>
                    </div>
                </template>
            </StepByStep>
    </div>
</template>

<script setup>
    import { ref, computed, watch } from 'vue';

    import StepByStep from '@Component/steps/StepByStep.vue';
    import FileSelector from '@Component/files/FileSelector.vue';
    import QuillEditor from '@Component/forms/QuillEditor.vue';

    import { mockAvailableDocuments } from '@/data/mockData';
    import {
        FILE_ICONS,
        AUTOMATION_FREQUENCY_OPTIONS,
        AUTOMATION_WEEKDAYS,
        AUTOMATION_MONTHLY_TYPE_OPTIONS,
        AUTOMATION_CONFIG
    } from '@/constants';

    const emit = defineEmits(['automation-created']);

    const stepByStepRef = ref(null);
    const completedSteps = ref([]);

    // Dados do formulário
    const formData = ref({
        name: '',
        description: '',
        active: true,
        selectedDocuments: [],
        emailSubject: '',
        emailMessage: '',
        recipients: [],
        frequency: '',
        time: AUTOMATION_CONFIG.DEFAULT_TIME,
        date: '',
        weekdays: [],
        monthlyType: 'day',
        monthDay: 1
    });

    // Steps da automação
    const automationSteps = ref([
        {
            title: 'Configurações',
            subtitle: 'Informações básicas'
        },
        {
            title: 'Arquivos',
            subtitle: 'Documentos para envio'
        },
        {
            title: 'Email',
            subtitle: 'Configurações de email'
        },
        {
            title: 'Frequência',
            subtitle: 'Quando enviar'
        },
        {
            title: 'Revisão',
            subtitle: 'Confirmar configurações'
        }
    ]);

    // Opções usando constantes
    const frequencyOptions = ref(AUTOMATION_FREQUENCY_OPTIONS);
    const weekdays = ref(AUTOMATION_WEEKDAYS);
    const monthlyTypeOptions = ref(AUTOMATION_MONTHLY_TYPE_OPTIONS);

    // Computeds para controlar exibição
    const showTimeConfig = computed(() => {
        return ['once', 'daily', 'weekly', 'monthly'].includes(formData.value.frequency);
    });

    const showDateConfig = computed(() => {
        return ['once', 'daily'].includes(formData.value.frequency);
    });

    const frequencySummary = computed(() => {
        if (!formData.value.frequency) return '';

        const frequency = formData.value.frequency;
        const time = formData.value.time;
        const date = formData.value.date;

        switch (frequency) {
            case 'once':
                return date && time
                    ? `Será enviado uma única vez no dia ${formatDate(date)} às ${time}`
                    : 'Configure a data e horário para execução única';
            case 'daily':
                return time
                    ? `Será enviado diariamente às ${time}`
                    : 'Configure o horário para envio diário';
            case 'weekly':
                const selectedDays = formData.value.weekdays;
                if (selectedDays.length === 0) return 'Selecione os dias da semana';
                const dayLabels = selectedDays.map(day =>
                    weekdays.value.find(w => w.value === day)?.label
                ).join(', ');
                return time
                    ? `Será enviado às ${time} nos dias: ${dayLabels}`
                    : `Configure o horário para envio nos dias: ${dayLabels}`;
            case 'monthly':
                return time
                    ? `Será enviado mensalmente às ${time}`
                    : 'Configure o horário para envio mensal';
            default:
                return '';
        }
    });

    // Validações de cada step
    const validationRules = computed(() => ({
        0: () => canProceedStep1.value,
        1: () => canProceedStep2.value,
        2: () => canProceedStep3.value,
        3: () => canProceedStep4.value
    }));

    // Validações específicas
    const canProceedStep1 = computed(() => {
        return formData.value.name?.trim().length > 0;
    });

    const canProceedStep2 = computed(() => {
        return formData.value.selectedDocuments?.length > 0;
    });

    const canProceedStep3 = computed(() => {
        return formData.value.emailSubject?.trim().length > 0 &&
               formData.value.recipients?.length > 0;
    });

    const canProceedStep4 = computed(() => {
        if (formData.value.frequency === 'once') {
            return formData.value.date?.length > 0 && formData.value.time?.length > 0;
        } else if (['daily', 'weekly', 'monthly'].includes(formData.value.frequency)) {
            let isValid = formData.value.frequency?.length > 0 && formData.value.time?.length > 0;

            if (formData.value.frequency === 'weekly') {
                isValid = isValid && formData.value.weekdays?.length > 0;
            }

            return isValid;
        }
        return false;
    });

    // Arquivos selecionados para revisão
    const selectedFilesForReview = computed(() => {
        return formData.value.selectedDocuments
            .map(docId => mockAvailableDocuments.find(doc => doc.id === docId))
            .filter(doc => doc);
    });

    // Métodos
    const handleStepChange = (step) => {
        console.log('Step changed to:', step);
    };

    const toggleWeekday = (day) => {
        const index = formData.value.weekdays.indexOf(day);
        if (index > -1) {
            formData.value.weekdays.splice(index, 1);
        } else {
            formData.value.weekdays.push(day);
        }
    };

    const onFrequencyChange = () => {
        // Reset specific frequency configs
        formData.value.weekdays = [];
        formData.value.monthlyType = 'day';
        formData.value.monthDay = 1;
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('pt-BR');
    };

    const getDocumentIcon = (type) => {
        return FILE_ICONS[type] || 'file-text';
    };


    const getFrequencyLabel = () => {
        const option = frequencyOptions.value.find(opt => opt.value === formData.value.frequency);
        return option ? option.label : '';
    };

    const formatDateTime = (dateTimeString) => {
        if (!dateTimeString) return '';
        const date = new Date(dateTimeString);
        return date.toLocaleString('pt-BR');
    };

    const createAutomation = () => {
        const newAutomation = {
            id: Date.now(),
            ...formData.value,
            createdAt: new Date().toISOString(),
            lastRun: null,
            nextRun: calculateNextRun()
        };

        emit('automation-created', newAutomation);
        resetForm();
    };

    const calculateNextRun = () => {
        if (formData.value.frequency === 'once') {
            return `${formData.value.date}T${formData.value.time}:00`;
        }
        // Calcular próxima execução baseada na frequência
        return new Date().toISOString();
    };

    const resetForm = () => {
        formData.value = {
            name: '',
            description: '',
            active: true,
            selectedDocuments: [],
            emailSubject: '',
            emailMessage: '',
            recipients: [],
            frequency: '',
            time: AUTOMATION_CONFIG.DEFAULT_TIME,
            date: '',
            weekdays: [],
            monthlyType: 'day',
            monthDay: 1
        };
        completedSteps.value = [];
        if (stepByStepRef.value) {
            stepByStepRef.value.goToStep(0);
        }
    };

    const handleFinish = () => {
        createAutomation();
    };
</script>

<style lang="scss" scoped>
    @import '@/assets/css/mediaScreen.scss';

    .new-automation-modal {
        min-height: 600px;
    }

    .modal-section {
        margin-bottom: 2em;

        h4 {
            font-size: 1em;
            font-weight: 600;
            color: var(--textPrimary);
            margin: 0 0 1em 0;
        }
    }

    .radio-group {
        display: flex;
        gap: 2em;
        flex-wrap: wrap;
    }

    .step-actions {
        display: flex;
        gap: 1em;
        justify-content: center;
        margin-top: 3em;
        padding-top: 2em;
        border-top: 1px solid var(--borderColorLight);
    }

    // Seletor de dias da semana
    .weekdays-selector {
        display: flex;
        gap: 0.5em;
        flex-wrap: wrap;
        margin-top: 0.5em;
    }

    .weekday-item {
        padding: 0.75em 1em;
        border: 2px solid var(--borderColorLight);
        border-radius: 0.5em;
        cursor: pointer;
        font-size: 0.875em;
        font-weight: 600;
        text-align: center;
        min-width: 3em;
        transition: all 0.2s ease;
        background: white;

        &:hover {
            border-color: var(--linkColor);
            background: rgba(59, 130, 246, 0.05);
        }

        &.selected {
            background: var(--linkColor);
            color: white;
            border-color: var(--linkColor);
        }
    }

    // Seções de revisão
    .review-sections {
        display: flex;
        flex-direction: column;
        gap: 2em;
    }

    .review-section {
        padding: 1.5em;
        background: rgba(59, 130, 246, 0.02);
        border: 1px solid rgba(59, 130, 246, 0.1);
        border-radius: 0.75em;

        h4 {
            font-size: 1.125em;
            font-weight: 600;
            color: var(--linkColor);
            margin: 0 0 1.25em 0;
            padding-bottom: 0.5em;
            border-bottom: 1px solid rgba(59, 130, 246, 0.1);
        }
    }

    .review-item {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 0.75em;
        gap: 1em;

        &:last-child {
            margin-bottom: 0;
        }
    }

    .review-label {
        font-weight: 600;
        color: var(--textPrimary);
        min-width: 100px;
        flex-shrink: 0;
    }

    .review-value {
        color: var(--textSecondary);
        text-align: right;
        flex: 1;

        &.status-badge {
            padding: 0.25em 0.75em;
            border-radius: 1em;
            font-size: 0.8125em;
            font-weight: 600;
            background: rgba(245, 158, 11, 0.1);
            color: #f59e0b;

            &.active {
                background: rgba(16, 185, 129, 0.1);
                color: #10b981;
            }
        }
    }

    // Lista de arquivos selecionados
    .selected-files-list {
        display: flex;
        flex-direction: column;
        gap: 0.75em;
    }

    .file-item {
        display: flex;
        align-items: center;
        gap: 0.75em;
        padding: 0.75em;
        background: white;
        border: 1px solid var(--borderColorLight);
        border-radius: 0.5em;

        .file-name {
            font-weight: 600;
            color: var(--textPrimary);
            flex: 1;
        }

        .file-type {
            font-size: 0.8125em;
            color: var(--textSecondary);
            background: rgba(107, 114, 128, 0.1);
            padding: 0.25em 0.5em;
            border-radius: 0.25em;
        }
    }

    // Resumo da frequência
    .frequency-summary {
        margin-top: 1.5em;

        .summary-card {
            display: flex;
            align-items: flex-start;
            gap: 1em;
            padding: 1em;
            background: rgba(59, 130, 246, 0.05);
            border: 1px solid rgba(59, 130, 246, 0.1);
            border-radius: 0.75em;
        }

        .summary-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 2em;
            height: 2em;
            background: rgba(59, 130, 246, 0.1);
            border-radius: 50%;
            color: var(--linkColor);
            flex-shrink: 0;

            sw-icon {
                width: 1em;
                height: 1em;
            }
        }

        .summary-content {
            flex: 1;
            display: flex;
            flex-direction: column;
            gap: 0.25em;
        }

        .summary-label {
            font-size: 0.875em;
            font-weight: 600;
            color: var(--linkColor);
        }

        .summary-text {
            font-size: 0.875em;
            color: var(--textSecondary);
            line-height: 1.4;
        }
    }

    // Labels de formulário
    .form-label {
        display: block;
        font-size: 0.875em;
        font-weight: 600;
        color: var(--textPrimary);
        margin-bottom: 0.5em;
    }

    .form-help {
        display: block;
        font-size: 0.75em;
        color: var(--textSecondary);
        margin-top: 0.25em;
        line-height: 1.4;
    }

    // Responsividade
    @include screen_medias(s) {
        .step-form {
            padding: 1em;
        }


        .radio-group {
            gap: 1em;
        }

        .step-actions {
            flex-direction: column;
            gap: 0.75em;
        }

        .weekdays-grid {
            gap: 0.375em;
        }

        .weekday-item {
            padding: 0.625em 0.75em;
            font-size: 0.8125em;
            min-width: 2.5em;
        }

        .review-item {
            flex-direction: column;
            align-items: flex-start;
            gap: 0.5em;
        }

        .review-value {
            text-align: left;
        }
    }
</style>